import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useState,
} from 'react';
import {
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import CardWrapper from 'components/Financial/CardWrapper';
import { FormikProvider, useFormik } from 'formik';
import AutoCompleteContext from 'components/CustomInputsInFormikContext/AutoComplete';
import NumberFormatContext from 'components/CustomInputsInFormikContext/NumberFormat';
import InstallmentInput from './components/InstallmentInput';
import Installments from './components/Installments';
import { billSchema } from './schemas';
import api from 'services/api';
import { isValidDate, labelField, roundNumber } from 'utils';
import {
  filterUnpaidInstallments, mapValueAdjustments, sumOfInstallments, sumOfInstallmentsPaid,
} from './helpers';
import { useLoadDataByLocation } from 'hooks/useLoadDataByLocation';
import { useParams } from 'react-router-dom';

const initialValues = {
  issueDate: new Date(),
  status: 'pendente',
  stakeholder: null,
  value: 0,
  qtyParts: 1,
  parts: [
    {
      bankAccount: '',
      expirationDate: new Date(),
      part: 0,
      pay: false,
      paymentDate: null,
      paymentType: '',
      value: 0,
    },
  ],
};

function Bill({
  type,
  description,
  totalPrice,
  disabledTotalValue,
  disabledStakeholder,
  getBillValue = () => {},
  callback = () => {},
}, ref) {
  const { id } = useParams();

  const [providers, setProviders] = useState([]);
  const formik = useFormik({
    initialValues,
    validationSchema: billSchema(disabledStakeholder),
  });

  function setData(data) {
    const bill = data.bill ?? data;

    const partsMapped = bill?.parts?.map((part) => ({
      ...part,
      expirationDate: isValidDate(part?.expirationDate),
      paymentDate: isValidDate(part?.paymentDate),
      bankAccount: part.bankAccount?.id,
      paymentType: part.paymentType?.id,
    })).sort((a, b) => a.part - b.part);

    const stakeholder = disabledStakeholder ? {} : { stakeholder: bill.stakeholder };

    formik.setValues({
      ...stakeholder,
      issueDate: isValidDate(bill.issueDate),
      status: bill.status,
      qtyParts: bill.qtyParts,
      value: bill.value,
      parts: partsMapped,
    });
  }

  useLoadDataByLocation({ setData });

  useEffect(() => {
    async function loadData() {
      try {
        const response = (await api.get('/providers')).data;
        setProviders(response);
      } catch (err) {
        throw new Error();
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    callback({ isValid: formik.isValid, values: formik.values });
  }, [callback, formik.isValid, formik.values]);

  useEffect(() => {
    getBillValue(formik.values.value);
  }, [formik.values.value, getBillValue]);

  useEffect(() => {
    if (totalPrice) {
      formik.setFieldValue('value', totalPrice);
    }
  }, [totalPrice]);

  // verifica se todas as parcelas estão pagas
  const checkAllInstallmentsPaid = useCallback(() => {
    const paid = formik.values.parts.every((part) => part.pay);
    if (paid) {
      formik.values.status = 'concluída';
    }
  }, [formik.values]);

  useEffect(() => {
    checkAllInstallmentsPaid();
  }, [checkAllInstallmentsPaid]);

  const handleSubmit = async () => {
    try {
      await formik.submitForm();
    } catch (e) {}
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  const updateInstallment = () => {
    const { parts, value } = formik.values;
    const fixedValue = sumOfInstallmentsPaid(parts);

    const medValue = roundNumber(
      (value - fixedValue)
        / (filterUnpaidInstallments(parts).length),
    );

    const mappedArray = mapValueAdjustments(parts, medValue);

    const newItemValue = value - sumOfInstallments(mappedArray);

    mappedArray[mappedArray.length - 1].value += newItemValue;

    formik.setFieldValue('parts', [
      ...mappedArray,
    ]);
  };

  const responsivenessStakeholder = {
    xs: 12,
    sm: 6,
    md: 12,
    lg: 4,
    xl: true,
  };
  const responsivenessValue = {
    xs: 12,
    sm: disabledStakeholder ? 12 : 6,
    lg: 4,
    xl: disabledStakeholder ? 3 : true,
  };

  const responsivenessInstallmentInput = {
    xs: 12,
    sm: disabledStakeholder ? 12 : 6,
    lg: 4,
    xl: 3,
  };

  return (
    <FormikProvider value={formik}>
      <CardWrapper title="Dados do Pagamento">
        {!disabledStakeholder && (
        <Grid item {...responsivenessStakeholder}>
          <AutoCompleteContext
            label={labelField('Fornecedor')}
            name="stakeholder"
            getOptionLabel={(option) => option.name}
            options={providers}
          />
        </Grid>
        )}
        <Grid item {...responsivenessValue}>
          <NumberFormatContext
            disabled={id || disabledTotalValue}
            fieldSize={20}
            label="Valor Total"
            name="value"
            prefix="R$ "
            decimalScale={2}
            value={formik.values.value}
            onValueChangeCallback={() => {
              if (totalPrice) {
                updateInstallment();
              }
            }}
            onBlurCallback={() => {
              if (formik.values.value) {
                updateInstallment();
              }
            }}
          />
        </Grid>
        <Grid item {...responsivenessInstallmentInput}>
          <InstallmentInput
            disabledField={id}
          />
        </Grid>
        <Grid item xs={12} mt={1}>
          <Divider orientation="horizontal" flexItem variant="fullWidth" />
        </Grid>

        {/* Parcelas */}
        <Grid container spacing={2} item>
          <Grid item xs={12}>
            <Typography fontSize={14} fontWeight="bold">Parcelamento</Typography>
          </Grid>
          <Grid item xs={12}>
            <Installments
              type={type}
              description={description}
            />
          </Grid>
        </Grid>
      </CardWrapper>
    </FormikProvider>
  );
}

export default forwardRef(Bill);
